import type { CompetencyCode, KnowledgeCode, MisconceptionCode, Question } from '../types'
import { SeedGenerator } from './generators/math_generators'

type TrigSeed = {
  id: string
  amplitude: number
  midline: number
  frequency: number
  phase: number
  wheelRadius: number
  wheelCenter: number
  wheelPeriod: number
  gearSmall: number
  gearLarge: number
  breathingRate: number
}

const option = (id: string, text: string) => ({ id, text })

const base = (seed: TrigSeed, topic: string, competency: CompetencyCode[] = ['quantitative-reasoning', 'problem-solving']) => ({
  exam: 'VACT' as const,
  domain: 'Toán học',
  topic,
  knowledge: ['trigonometry'] as KnowledgeCode[],
  competency,
  prerequisites: ['trigonometry'] as KnowledgeCode[],
  misconceptionOptions: ['concept-gap', 'calculation-slip', 'strategy-choice', 'careless-reading'] as MisconceptionCode[],
  tags: ['original', 'authored-variant', 'k11', 'chapter-1', 'trigonometry', 'conictypst-k11', 'parameterized'],
  aiContext: JSON.stringify(seed),
})

function distinctOptions(correct: string, distractors: string[]) {
  const values = [correct, ...distractors]
  const used = new Set<string>()
  return values.map((value, index) => {
    let result = value
    let bump = 1
    while (used.has(result)) result = `${value} + ${bump++}`
    used.add(result)
    return option(['A', 'B', 'C', 'D'][index], result)
  })
}

export function generateTrigonometrySeed(id: string): TrigSeed {
  const gen = new SeedGenerator(id)
  const wheelRadius = [12, 15, 18, 21][gen.randomInt(0, 3)]
  return {
    id,
    amplitude: [3, 4, 5, 6][gen.randomInt(0, 3)],
    midline: [8, 10, 12, 15][gen.randomInt(0, 3)],
    frequency: [1, 2, 3, 4][gen.randomInt(0, 3)],
    phase: [0, 1, 2, 3][gen.randomInt(0, 3)],
    wheelRadius,
    wheelCenter: wheelRadius + [8, 10, 12][gen.randomInt(0, 2)],
    wheelPeriod: [16, 20, 24, 30][gen.randomInt(0, 3)],
    gearSmall: [8, 10, 12, 15][gen.randomInt(0, 3)],
    gearLarge: [20, 24, 30, 36][gen.randomInt(0, 3)],
    breathingRate: [12, 15, 18, 20][gen.randomInt(0, 3)],
  }
}

function periodicModelQuestions(seed: TrigSeed): Question[] {
  const { amplitude: a, midline: d, frequency: n, phase: p } = seed
  const lo = d - a
  const hi = d + a
  const period = `\\dfrac{2\\pi}{${n}}`
  const peakX = `\\dfrac{\\pi/2${p ? `-${p}` : ''}}{${n}}`
  return [
    {
      ...base(seed, 'Hàm số lượng giác – biên độ, chu kì và miền giá trị'),
      id: `VACT-K11-TRIG-MODEL-${seed.id}-01`, difficulty: 3, cognitiveLevel: 'M2', type: 'single-choice',
      stem: `Tập giá trị của hàm số $y=${a}\\sin(${n}x${p >= 0 ? '+' : ''}${p})+${d}$ là`,
      choices: distinctOptions(`$[${lo};${hi}]$`, [`$[-${a};${a}]$`, `$[${d - n};${d + n}]$`, `$[${lo - 1};${hi + 1}]$`]),
      answer: 'A',
      solution: `Vì $-1\\le\\sin(${n}x${p >= 0 ? '+' : ''}${p})\\le1$, nên $-${a}\\le${a}\\sin(\\cdots)\\le${a}$. Cộng ${d} vào ba vế được $${lo}\\le y\\le${hi}$.`,
      hint1: 'Hệ số đứng trước sin quyết định biên độ; hằng số bên ngoài quyết định trục giữa.',
      hint2: `Biến thiên quanh ${d} một biên độ ${a}.`,
      estimatedSeconds: 105, variantGroup: 'k11-trig-range-v1',
    },
    {
      ...base(seed, 'Hàm số lượng giác – biên độ, chu kì và miền giá trị'),
      id: `VACT-K11-TRIG-MODEL-${seed.id}-02`, difficulty: 3, cognitiveLevel: 'M2', type: 'single-choice',
      stem: `Chu kì dương nhỏ nhất của $y=${a}\\sin(${n}x${p >= 0 ? '+' : ''}${p})+${d}$ là`,
      choices: distinctOptions(`$${period}$`, ['$2\\pi$', `$\\dfrac{\\pi}{${n}}$`, `$\\dfrac{2\\pi}{${a}}$`]),
      answer: 'A',
      solution: `Pha ban đầu và tịnh tiến đứng không làm thay đổi chu kì. Với $\\sin(${n}x+\\varphi)$, chu kì là $T=\\dfrac{2\\pi}{|${n}|}=${period}$.`,
      hint1: 'Chỉ hệ số của x trong đối số sin quyết định chu kì.',
      hint2: 'Dùng công thức $T=2\\pi/|a|$ cho $\\sin(ax+b)$.',
      estimatedSeconds: 95, variantGroup: 'k11-trig-period-v1',
    },
    {
      ...base(seed, 'Hàm số lượng giác – thời điểm đạt cực đại', ['pattern-recognition', 'quantitative-reasoning', 'problem-solving']),
      id: `VACT-K11-TRIG-MODEL-${seed.id}-03`, difficulty: 4, cognitiveLevel: 'M3', type: 'single-choice',
      stem: `Giá trị cực đại đầu tiên của hàm số trên với $x>0$ đạt được tại nghiệm dương nhỏ nhất của dạng`,
      choices: distinctOptions(`$x=${peakX}$ (cộng thêm bội của $${period}$ nếu cần để dương)`, [`$x=\\dfrac{${p}}{${n}}$`, `$x=\\dfrac{\\pi}{${n}}${p ? `-${p}` : ''}$`, `$x=${period}$`]),
      answer: 'A',
      solution: `Hàm đạt cực đại khi $\\sin(${n}x+${p})=1$, tức ${n}x+${p}=\\dfrac\\pi2+2k\\pi$. Suy ra $x=\\dfrac{\\pi/2-${p}+2k\\pi}{${n}}$. Chọn $k$ nhỏ nhất để x dương; biểu thức này tương đương với phương án A.`,
      hint1: 'Sin đạt 1 khi đối số bằng $\\pi/2+2k\\pi$.',
      hint2: 'Sau khi giải theo x, cần chọn k phù hợp điều kiện x > 0.',
      estimatedSeconds: 175, variantGroup: 'k11-trig-max-time-v1',
    },
  ]
}

function equationQuestions(seed: TrigSeed): Question[] {
  const { phase: p } = seed
  const shift = p === 0 ? '' : (p > 0 ? `+${p}` : `${p}`)
  return [
    {
      ...base(seed, 'Phương trình lượng giác – đếm nghiệm trên đoạn', ['logical-reasoning', 'problem-solving']),
      id: `VACT-K11-TRIG-EQ-${seed.id}-01`, difficulty: 4, cognitiveLevel: 'M3', type: 'single-choice',
      stem: `Số nghiệm của phương trình $\\sin(x${shift})=\\dfrac12$ trên đoạn $[0;4\\pi]$ là`,
      choices: [option('A', '2'), option('B', '3'), option('C', '4'), option('D', '5')],
      answer: 'C',
      solution: `Trong mỗi chu kì dài $2\\pi$, phương trình $\\sin u=1/2$ có đúng hai nghiệm. Đoạn $[0;4\\pi]$ gồm đúng hai chu kì; phép tịnh tiến pha không đổi số nghiệm trên một khoảng gồm nguyên số chu kì. Vậy có $2\\times2=4$ nghiệm.`,
      hint1: 'Đổi biến $u=x+\\varphi$; số nghiệm không đổi qua tịnh tiến.',
      hint2: 'Trên một chu kì, sin bằng 1/2 tại hai vị trí.',
      estimatedSeconds: 145, variantGroup: 'k11-trig-equation-count-v1',
    },
    {
      ...base(seed, 'Phương trình lượng giác – hạ bậc và đếm nghiệm', ['logical-reasoning', 'problem-solving']),
      id: `VACT-K11-TRIG-EQ-${seed.id}-02`, difficulty: 5, cognitiveLevel: 'M3', type: 'single-choice',
      stem: 'Phương trình $\\cos 2x=\\sin x$ có bao nhiêu nghiệm thuộc đoạn $[0;2\\pi]$?',
      choices: [option('A', '1'), option('B', '2'), option('C', '3'), option('D', '4')],
      answer: 'C',
      solution: `Đặt $t=\\sin x$. Vì $\\cos2x=1-2\\sin^2x$, phương trình trở thành $1-2t^2=t$, hay $(2t-1)(t+1)=0$. Do đó $\\sin x=1/2$ cho 2 nghiệm, còn $\\sin x=-1$ cho 1 nghiệm trên $[0;2\\pi]$. Tổng cộng có 3 nghiệm.`,
      hint1: 'Đưa cos 2x về biểu thức theo sin x.',
      hint2: 'Sau khi có sin x = 1/2 hoặc −1, đếm riêng từng trường hợp.',
      estimatedSeconds: 200, variantGroup: 'k11-trig-double-angle-v1',
    },
  ]
}

function circularMotionQuestions(seed: TrigSeed): Question[] {
  const { wheelRadius: r, wheelCenter: c, wheelPeriod: t } = seed
  const angular = `\\dfrac{2\\pi}{${t}}`
  return [
    {
      ...base(seed, 'Mô hình tuần hoàn – vòng quay quan sát'),
      id: `VACT-K11-TRIG-WHEEL-${seed.id}-01`, difficulty: 4, cognitiveLevel: 'M2', type: 'single-choice',
      passageTitle: 'Cabin vòng quay',
      passageText: `Một cabin xuất phát ở vị trí thấp nhất. Độ cao (m) sau t phút được mô hình bởi $h(t)=${c}-${r}\\cos(${angular}t)$.`,
      stem: 'Bán kính của vòng quay là',
      choices: distinctOptions(`${r} m`, [`${c} m`, `${c - r} m`, `${2 * r} m`]),
      answer: 'A',
      solution: `Trong mô hình $h(t)=C-R\\cos(\\omega t)$, biên độ R chính là bán kính. Ở đây biên độ là ${r}, nên bán kính vòng quay bằng ${r} m.`,
      hint1: 'Độ cao dao động quanh độ cao tâm với biên độ bằng bán kính.',
      hint2: 'Đọc hệ số đứng trước cos.',
      estimatedSeconds: 100, variantGroup: 'k11-trig-ferris-v1',
    },
    {
      ...base(seed, 'Mô hình tuần hoàn – vòng quay quan sát', ['modelling', 'quantitative-reasoning', 'problem-solving']),
      id: `VACT-K11-TRIG-WHEEL-${seed.id}-02`, difficulty: 4, cognitiveLevel: 'M3', type: 'single-choice',
      stem: 'Lần đầu tiên sau khi xuất phát cabin ở đúng độ cao tâm vòng quay là sau',
      choices: distinctOptions(`${t / 4} phút`, [`${t / 2} phút`, `${t} phút`, `${(3 * t) / 4} phút`]),
      answer: 'A',
      solution: `Cabin ở độ cao tâm khi $\\cos(${angular}t)=0$. Lần đầu tiên xảy ra tại góc $\\pi/2$, tức ${angular}t=\\pi/2$. Suy ra $t=${t}/4=${t / 4}$ phút.`,
      hint1: 'Ở độ cao tâm thì phần cos phải bằng 0.',
      hint2: 'Nghiệm dương nhỏ nhất của cos u = 0 là u = π/2.',
      estimatedSeconds: 145, variantGroup: 'k11-trig-ferris-v1',
    },
  ]
}

function waveAndGearQuestions(seed: TrigSeed): Question[] {
  const { breathingRate: rate, gearSmall: s, gearLarge: l } = seed
  const period = 60 / rate
  const turns = l / s
  return [
    {
      ...base(seed, 'Dao động điều hòa – chu kì và tần số', ['modelling', 'quantitative-reasoning']),
      id: `VACT-K11-TRIG-WAVE-${seed.id}-01`, difficulty: 4, cognitiveLevel: 'M2', type: 'single-choice',
      passageTitle: 'Nhịp thở',
      passageText: `Một người có ${rate} chu kì hô hấp trong một phút. Thể tích khí có thể được mô hình hóa bởi một hàm sin theo thời gian t (giây).`,
      stem: 'Chu kì của mô hình theo đơn vị giây là',
      choices: distinctOptions(`${period} giây`, [`${rate} giây`, `${60 * rate} giây`, `${period / 2} giây`]),
      answer: 'A',
      solution: `Trong 60 giây có ${rate} chu kì, nên thời gian của một chu kì là $T=60/${rate}=${period}$ giây.`,
      hint1: 'Chu kì là thời gian cho một lần lặp, không phải số lần lặp trong một phút.',
      hint2: 'Lấy 60 chia cho số chu kì/phút.',
      estimatedSeconds: 105, variantGroup: 'k11-trig-wave-period-v1',
    },
    {
      ...base(seed, 'Chuyển động quay – liên hệ góc quét', ['modelling', 'quantitative-reasoning', 'problem-solving']),
      id: `VACT-K11-TRIG-GEAR-${seed.id}-01`, difficulty: 4, cognitiveLevel: 'M3', type: 'single-choice',
      passageTitle: 'Hai bánh răng không trượt',
      passageText: `Một bánh răng nhỏ bán kính ${s} cm ăn khớp không trượt với bánh răng lớn bán kính ${l} cm.`,
      stem: `Khi bánh nhỏ quay đúng 1 vòng, bánh lớn quay một góc có độ lớn bằng`,
      choices: distinctOptions(`$\\dfrac{${2 * s}\\pi}{${l}}$ rad`, [`$\\dfrac{${2 * l}\\pi}{${s}}$ rad`, `$2\\pi$ rad`, `$\\dfrac{${s}\\pi}{${l}}$ rad`]),
      answer: 'A',
      solution: `Không trượt nên hai cung đi được bằng nhau: ${s}\\cdot2\\pi=${l}\\cdot\\theta$. Do đó $\\theta=\\dfrac{${2 * s}\\pi}{${l}}$ rad. Bánh lớn quay ít hơn vì bán kính lớn hơn.`,
      hint1: 'Không trượt nghĩa là độ dài cung ở vành hai bánh bằng nhau.',
      hint2: `Viết ${s}·2π = ${l}·θ.`,
      estimatedSeconds: 160, variantGroup: 'k11-trig-gears-v1',
    },
    {
      ...base(seed, 'Chuyển động quay – tỉ số số vòng', ['modelling', 'logical-reasoning', 'problem-solving']),
      id: `VACT-K11-TRIG-GEAR-${seed.id}-02`, difficulty: 4, cognitiveLevel: 'M3', type: 'single-choice',
      stem: `Để bánh lớn quay đúng 1 vòng, bánh nhỏ phải quay bao nhiêu vòng?`,
      choices: distinctOptions(`${turns}`, [`${s / l}`, `${turns + 1}`, `${Math.max(1, turns - 1)}`]),
      answer: 'A',
      solution: `Một vòng của bánh lớn tương ứng độ dài vành $2\\pi\\cdot${l}$. Mỗi vòng bánh nhỏ đi được $2\\pi\\cdot${s}$. Số vòng cần là ${l}/${s}=${turns}.`,
      hint1: 'So sánh độ dài vành của hai bánh răng.',
      hint2: 'Bánh nhỏ phải quay nhiều vòng hơn, tỉ lệ bán kính lớn/bé.',
      estimatedSeconds: 145, variantGroup: 'k11-trig-gears-v1',
    },
  ]
}

/** 10 source-derived, re-authored trigonometry families per deterministic seed. */
export function k11TrigonometryQuestions(seed: TrigSeed): Question[] {
  return [
    ...periodicModelQuestions(seed),
    ...equationQuestions(seed),
    ...circularMotionQuestions(seed),
    ...waveAndGearQuestions(seed),
  ]
}
